import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Níveis de dificuldade da IA
 */
export type AIDifficulty = "easy" | "normal" | "hard" | "extreme";

/**
 * Configurações de dificuldade para cada nível
 */
export interface DifficultyConfig {
  /** Multiplicador de aiLevel base dos inimigos */
  aiLevelMultiplier: number;
  /** Multiplicador de dano dos inimigos */
  damageMultiplier: number;
  /** Multiplicador de velocidade dos inimigos */
  speedMultiplier: number;
  /** Multiplicador de taxa de disparo (menor = mais rápido) */
  fireRateMultiplier: number;
  /** Multiplicador de HP dos inimigos */
  healthMultiplier: number;
  /** Multiplicador de quantidade de inimigos */
  enemyCountMultiplier: number;
  /** Multiplicador de recompensas */
  rewardMultiplier: number;
  /** Nome localizado */
  labelKey: string;
  /** Descrição localizada */
  descriptionKey: string;
  /** Cor do tema */
  color: string;
  /** Emoji */
  emoji: string;
}

/**
 * Configurações predefinidas para cada dificuldade
 */
export const DIFFICULTY_CONFIGS: Record<AIDifficulty, DifficultyConfig> = {
  easy: {
    aiLevelMultiplier: 0.5,
    damageMultiplier: 0.7,
    speedMultiplier: 0.8,
    fireRateMultiplier: 1.5,
    healthMultiplier: 0.8,
    enemyCountMultiplier: 0.8,
    rewardMultiplier: 0.7,
    labelKey: "difficulty.easy",
    descriptionKey: "difficulty.easyDesc",
    color: "#4ade80", // verde
    emoji: "🌱",
  },
  normal: {
    aiLevelMultiplier: 1.0,
    damageMultiplier: 1.0,
    speedMultiplier: 1.0,
    fireRateMultiplier: 1.0,
    healthMultiplier: 1.0,
    enemyCountMultiplier: 1.0,
    rewardMultiplier: 1.0,
    labelKey: "difficulty.normal",
    descriptionKey: "difficulty.normalDesc",
    color: "#22d3ee", // ciano
    emoji: "⚔️",
  },
  hard: {
    aiLevelMultiplier: 1.5,
    damageMultiplier: 1.3,
    speedMultiplier: 1.2,
    fireRateMultiplier: 0.8,
    healthMultiplier: 1.3,
    enemyCountMultiplier: 1.2,
    rewardMultiplier: 1.3,
    labelKey: "difficulty.hard",
    descriptionKey: "difficulty.hardDesc",
    color: "#f97316", // laranja
    emoji: "🔥",
  },
  extreme: {
    aiLevelMultiplier: 2.0,
    damageMultiplier: 1.6,
    speedMultiplier: 1.4,
    fireRateMultiplier: 0.6,
    healthMultiplier: 1.6,
    enemyCountMultiplier: 1.4,
    rewardMultiplier: 1.8,
    labelKey: "difficulty.extreme",
    descriptionKey: "difficulty.extremeDesc",
    color: "#ef4444", // vermelho
    emoji: "💀",
  },
};

interface DifficultyState {
  /** Dificuldade atual selecionada */
  difficulty: AIDifficulty;
  
  /** Define a dificuldade */
  setDifficulty: (difficulty: AIDifficulty) => void;
  
  /** Obtém a configuração atual */
  getConfig: () => DifficultyConfig;
  
  /** Aplica multiplicador de aiLevel baseado na dificuldade */
  adjustAILevel: (baseAILevel: number) => number;
  
  /** Aplica multiplicador de dano baseado na dificuldade */
  adjustDamage: (baseDamage: number) => number;
  
  /** Aplica multiplicador de velocidade baseado na dificuldade */
  adjustSpeed: (baseSpeed: number) => number;
  
  /** Aplica multiplicador de taxa de disparo baseado na dificuldade */
  adjustFireRate: (baseFireRate: number) => number;
  
  /** Aplica multiplicador de HP baseado na dificuldade */
  adjustHealth: (baseHealth: number) => number;
  
  /** Aplica multiplicador de quantidade de inimigos */
  adjustEnemyCount: (baseCount: number) => number;
  
  /** Aplica multiplicador de recompensas */
  adjustReward: (baseReward: number) => number;
}

export const useDifficulty = create<DifficultyState>()(
  persist(
    (set, get) => ({
      difficulty: "normal",
      
      setDifficulty: (difficulty) => set({ difficulty }),
      
      getConfig: () => DIFFICULTY_CONFIGS[get().difficulty],
      
      adjustAILevel: (baseAILevel) => {
        const config = DIFFICULTY_CONFIGS[get().difficulty];
        return Math.min(1, baseAILevel * config.aiLevelMultiplier);
      },
      
      adjustDamage: (baseDamage) => {
        const config = DIFFICULTY_CONFIGS[get().difficulty];
        return Math.round(baseDamage * config.damageMultiplier);
      },
      
      adjustSpeed: (baseSpeed) => {
        const config = DIFFICULTY_CONFIGS[get().difficulty];
        return baseSpeed * config.speedMultiplier;
      },
      
      adjustFireRate: (baseFireRate) => {
        const config = DIFFICULTY_CONFIGS[get().difficulty];
        return Math.round(baseFireRate * config.fireRateMultiplier);
      },
      
      adjustHealth: (baseHealth) => {
        const config = DIFFICULTY_CONFIGS[get().difficulty];
        return Math.round(baseHealth * config.healthMultiplier);
      },
      
      adjustEnemyCount: (baseCount) => {
        const config = DIFFICULTY_CONFIGS[get().difficulty];
        return Math.round(baseCount * config.enemyCountMultiplier);
      },
      
      adjustReward: (baseReward) => {
        const config = DIFFICULTY_CONFIGS[get().difficulty];
        return Math.round(baseReward * config.rewardMultiplier);
      },
    }),
    {
      name: "rocketanks-difficulty",
      version: 1,
    }
  )
);
